import React, { useState, useRef, useEffect, useMemo } from 'react';
import type { Model } from '../types';

export interface ModelDropdownProps {
  models: Model[];
  currentModel: Model | null;
  onSelect: (modelId: string) => void;
  disabled?: boolean;
}

const PROVIDER_DISPLAY_NAMES: Record<string, string> = {
  openai: 'OpenAI',
  anthropic: 'Anthropic',
  google: 'Google',
  mistral: 'Mistral',
  deepseek: 'DeepSeek',
  opensource: 'Open Source',
};

const PROVIDER_COLORS: Record<string, { bg: string; text: string; accent: string }> = {
  openai: {
    bg: 'bg-green-50 dark:bg-green-900/20',
    text: 'text-green-700 dark:text-green-400',
    accent: 'border-green-300 dark:border-green-700',
  },
  anthropic: {
    bg: 'bg-orange-50 dark:bg-orange-900/20',
    text: 'text-orange-700 dark:text-orange-400',
    accent: 'border-orange-300 dark:border-orange-700',
  },
  google: {
    bg: 'bg-blue-50 dark:bg-blue-900/20',
    text: 'text-blue-700 dark:text-blue-400',
    accent: 'border-blue-300 dark:border-blue-700',
  },
  mistral: {
    bg: 'bg-purple-50 dark:bg-purple-900/20',
    text: 'text-purple-700 dark:text-purple-400',
    accent: 'border-purple-300 dark:border-purple-700',
  },
  deepseek: {
    bg: 'bg-cyan-50 dark:bg-cyan-900/20',
    text: 'text-cyan-700 dark:text-cyan-400',
    accent: 'border-cyan-300 dark:border-cyan-700',
  },
};

const DEFAULT_COLORS = {
  bg: 'bg-gray-50 dark:bg-gray-800',
  text: 'text-gray-700 dark:text-gray-400',
  accent: 'border-gray-300 dark:border-gray-600',
};

function getProviderColors(provider: string) {
  return PROVIDER_COLORS[provider.toLowerCase()] || DEFAULT_COLORS;
}

function getProviderDisplayName(provider: string): string {
  return PROVIDER_DISPLAY_NAMES[provider.toLowerCase()] || provider.charAt(0).toUpperCase() + provider.slice(1);
}

function formatContextWindow(tokens: number): string {
  if (tokens >= 1000000) return `${(tokens / 1000000).toFixed(0)}M`;
  if (tokens >= 1000) return `${(tokens / 1000).toFixed(0)}K`;
  return tokens.toString();
}

export const ModelDropdown: React.FC<ModelDropdownProps> = ({
  models,
  currentModel,
  onSelect,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeProvider, setActiveProvider] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Group models by provider
  const groupedModels = useMemo(() => {
    const groups = new Map<string, Model[]>();
    for (const model of models) {
      const provider = model.provider.toLowerCase();
      if (!groups.has(provider)) {
        groups.set(provider, []);
      }
      groups.get(provider)!.push(model);
    }
    return groups;
  }, [models]);

  // Auto-select the current model's provider when opening
  useEffect(() => {
    if (isOpen && currentModel) {
      setActiveProvider(currentModel.provider.toLowerCase());
    }
    if (!isOpen) {
      setActiveProvider(null);
    }
  }, [isOpen, currentModel]);

  // Close on click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    }
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen]);

  const handleModelSelect = (modelId: string) => {
    onSelect(modelId);
    setIsOpen(false);
  };

  const currentProviderColors = currentModel
    ? getProviderColors(currentModel.provider)
    : DEFAULT_COLORS;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-all cursor-pointer
          ${isOpen
            ? 'bg-blue-100 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700 ring-2 ring-blue-200 dark:ring-blue-800'
            : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/30'
          }
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
        `}
      >
        <div className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0"></div>
        <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
          {currentModel ? currentModel.name : 'Select Model'}
        </span>
        <span className={`text-xs px-1.5 py-0.5 rounded ${currentProviderColors.bg} ${currentProviderColors.text}`}>
          {currentModel ? getProviderDisplayName(currentModel.provider) : ''}
        </span>
        <svg
          className={`w-4 h-4 text-blue-500 dark:text-blue-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown Panel — provider list + flyout */}
      {isOpen && (
        <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 flex items-start z-50">
          {/* Provider list */}
          <div className="w-52 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="px-4 py-2.5 border-b border-gray-100 dark:border-gray-700">
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Select Provider
              </p>
            </div>

            <div className="py-1">
              {Array.from(groupedModels.entries()).map(([provider, providerModels]) => {
                const colors = getProviderColors(provider);
                const hasActiveModel = providerModels.some(m => m.id === currentModel?.id);
                const isSelected = activeProvider === provider;

                return (
                  <div
                    key={provider}
                    onMouseEnter={() => setActiveProvider(provider)}
                    className={`
                      w-full flex items-center justify-between px-4 py-2.5 text-sm font-medium transition-colors cursor-default
                      hover:bg-gray-50 dark:hover:bg-gray-700/50
                      ${isSelected ? 'bg-gray-100 dark:bg-gray-700' : ''}
                      ${hasActiveModel && !isSelected ? colors.bg : ''}
                    `}
                  >
                    <div className="flex items-center gap-2.5">
                      <div className={`w-2 h-2 rounded-full ${hasActiveModel ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'}`}></div>
                      <span className={`font-semibold ${hasActiveModel ? colors.text : 'text-gray-700 dark:text-gray-300'}`}>
                        {getProviderDisplayName(provider)}
                      </span>
                      <span className="text-xs text-gray-400 dark:text-gray-500">
                        {providerModels.length}
                      </span>
                    </div>
                    <svg
                      className={`w-4 h-4 transition-colors ${isSelected ? 'text-blue-500' : 'text-gray-400'}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                );
              })}
            </div>

            {models.length === 0 && (
              <div className="px-4 py-6 text-center">
                <p className="text-sm text-gray-500 dark:text-gray-400">No models available</p>
              </div>
            )}
          </div>

          {/* Flyout submenu — to the right, visible when a provider is selected */}
          {activeProvider && groupedModels.has(activeProvider) && (
            <div className="ml-2 w-60 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className={`px-4 py-2 border-b ${getProviderColors(activeProvider).accent} ${getProviderColors(activeProvider).bg}`}>
                <p className={`text-xs font-semibold uppercase tracking-wider ${getProviderColors(activeProvider).text}`}>
                  {getProviderDisplayName(activeProvider)} Models
                </p>
              </div>

              <div className="py-1">
                {groupedModels.get(activeProvider)!.map(model => {
                  const isActive = model.id === currentModel?.id;
                  return (
                    <button
                      key={model.id}
                      onClick={() => model.available && handleModelSelect(model.id)}
                      disabled={!model.available}
                      className={`
                        w-full text-left px-4 py-2.5 text-sm transition-colors
                        ${isActive
                          ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                        }
                        ${!model.available ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}
                      `}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {isActive && (
                            <div className="w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0"></div>
                          )}
                          <span className={isActive ? 'font-semibold' : 'font-medium'}>
                            {model.name}
                          </span>
                        </div>
                        <span className="text-xs text-gray-400 dark:text-gray-500">
                          {formatContextWindow(model.contextWindow)} ctx
                        </span>
                      </div>
                      {!model.available && (
                        <span className="text-xs text-red-500 mt-0.5 block">Unavailable</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
